import { useState } from "react";
import toast from "react-hot-toast";
import {
  TargetIcon,
  VerticalMoreIcon,
  PlusRoundCircle,
} from "../../Assets/SvgIcons";
import Metric from "../../Components/Metric/Metric";
import { GoalCardWrapper, FloatingDropdownWrapper } from "./styles";
import { FloatingDropdown } from "../../Components/FloatingDropdown/FloatingDropdown";
import { useDeleteOrganizationGoalMutation } from "../../../redux/reducers/apiSlice";
import { Modal } from "../../Components/Modal/Modal";
import CreateOrEditModal from "../../Components/Modal/CreateOrEditMetricModal";
import { Metric as MetricInterface } from "../../../redux/reducers/types";
import { useOrganization } from "../../hooks/useOrganization";
import * as Sentry from "@sentry/react";
import TabbedMetricsModal from "../../Components/Modal/TabbedMetricModal";
import BlackFundMetric from "../../Components/BlackFundMetric";
import { Box } from "@chakra-ui/react";
import { useTierLimits, FREE_TIER_LIMITS } from "../../hooks/useTierLimits";

interface Props {
  goal: {
    name: string;
    description: string;
    id: string;
    metrics?: MetricInterface[];
  };
  sparkId: number | undefined;
  isAdmin: boolean;
  organizationId: string;
  isBlackFundGrantee?: boolean;
  onEdit: () => void;
}

export const GoalCard = ({
  goal,
  organizationId,
  isAdmin,
  sparkId,
  isBlackFundGrantee = false,
  onEdit,
}: Props) => {
  const [metricToEdit, setMetricToEdit] = useState<MetricInterface | undefined>(
    undefined
  );
  const [floatingDropdownVisible, setFloatingDropdownVisible] = useState(false);
  const [createMetricModalVisible, setCreateMetricModalVisible] =
    useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteGoal, { isLoading: deleteOrganizationGoalLoading }] =
    useDeleteOrganizationGoalMutation();
  
  const tierInfo = useTierLimits();
  const { isCurrentUserAdmin } = useOrganization();
  
  // Count total metrics (regular + black fund metrics)
  const totalMetrics =
    (goal.metrics?.length || 0) + (goal.black_fund_metrics?.length || 0);
  const maxMetrics = tierInfo.isPaid ? Infinity : FREE_TIER_LIMITS.MAX_METRICS_PER_SPARK;
  const canAddMetric = totalMetrics < maxMetrics;

  let adminDropdownItems = isAdmin
    ? [
        {
          label: "Edit goal",
          action: () => onEdit(),
        },
        {
          label: "Delete goal",
          isDestructiveAction: true,
          isLast: true,
          action: () => setDeleteModalVisible(true),
        },
      ]
    : [];

  const onDeleteGoal = async () => {
    try {
      await deleteGoal({
        orgId: organizationId,
        goalId: goal.id,
      }).unwrap();
    } catch (e) {
      Sentry.captureException(e);
      toast.error(
        "Something unexpected happen while trying to delete your goal. Try again later"
      );
    }
  };

  return (
    <GoalCardWrapper>
      <div className="top-row">
        <span className="title-and-icon">
          <TargetIcon />
          <p className="title">{goal.name}</p>
        </span>
        <FloatingDropdownWrapper>
          <FloatingDropdown
            visible={floatingDropdownVisible}
            onOutsideClicked={() => {
              setFloatingDropdownVisible(false);
            }}
            items={[
              {
                label: "Export",
                action: () => {},
              },
              ...adminDropdownItems,
            ]}
          />
        </FloatingDropdownWrapper>
        <div>
          <button
            className="options-button"
            disabled={!isCurrentUserAdmin}
            onClick={() => setFloatingDropdownVisible(true)}
          >
            <VerticalMoreIcon color={isCurrentUserAdmin ? "black" : "#eee"} />
          </button>
        </div>
      </div>
      <div>
        <p className="description">{goal.description}</p>
      </div>
      {goal.metrics?.map((metric) => (
        <Metric
          goalId={goal.id}
          orgId={organizationId}
          key={metric.id}
          metric={metric}
          onEdit={() => {
            setMetricToEdit(metric);
            setEditModalVisible(true);
          }}
          isAdmin={isCurrentUserAdmin}
        />
      ))}
      {isBlackFundGrantee &&
        goal.black_fund_metrics?.map((metric) => (
          <Box mt={8} key={metric.id}>
            <BlackFundMetric isAdmin={isCurrentUserAdmin} metric={metric} />
          </Box>
        ))}
      {isCurrentUserAdmin && canAddMetric && (
        <button
          onClick={() => {
            setMetricToEdit(undefined);
            setCreateMetricModalVisible(true);
          }}
          className="metric-button"
        >
          <PlusRoundCircle color={"black"} />
          <span className="metric-button-text">Add metric</span>
        </button>
      )}
      {isCurrentUserAdmin && !canAddMetric && !tierInfo.isPaid && (
        <div style={{ 
          padding: "12px", 
          background: "#FEF3C7", 
          border: "1px solid #FCD34D", 
          borderRadius: "8px",
          textAlign: "center",
          marginTop: "16px"
        }}>
          <p style={{ margin: 0, color: "#92400E", fontSize: "14px", fontWeight: "500" }}>
            ⚠️ Free tier limit: {totalMetrics}/{FREE_TIER_LIMITS.MAX_METRICS_PER_SPARK} metrics per goal
          </p>
          <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#78350F" }}>
            Upgrade to paid ($19/month) for unlimited metrics
          </p>
        </div>
      )}
      <CreateOrEditModal
          isOpen={editModalVisible}
          onClose={() => setEditModalVisible(false)}
          goalId={goal.id}
          sparkId={sparkId}
          orgId={organizationId}
          metric={metricToEdit}
        />
      <Modal
          isOpen={deleteModalVisible}
          onClose={() => setDeleteModalVisible(false)}
          primaryButtonText={`Delete goal`}
          secondaryButtonText={"Cancel"}
          modalTitle={"Are you sure you want to delete goal?"}
          onPrimaryButtonClicked={() => onDeleteGoal()}
          primaryLoading={deleteOrganizationGoalLoading}
          disablePrimaryButton={deleteOrganizationGoalLoading}
        />
      <TabbedMetricsModal
          orgId={organizationId}
          goalId={goal.id}
          isOpen={createMetricModalVisible}
          sparkId={sparkId}
          isBlackFundGrantee={isBlackFundGrantee}
          onClose={() => setCreateMetricModalVisible(false)}
        />
    </GoalCardWrapper>
  );
};
